/**
 * The tasks domain's FLAG MODULE (ruling 36 as amended 2026-08-29; converted in
 * flags fan-out batch 1).
 *
 * ONE DOOR, ONE FILE. The `defineFeatureFlag` descriptors and the named getters
 * that read them are CO-LOCATED here, and `@heidi/tasks/flags` publishes both.
 * `/contributions` carries nothing for flags — this domain has no such door at
 * all, and does not need one to declare a flag.
 *
 * ONE MINTING SITE PER FLAG, made structural rather than conventional by
 * co-location: each getter reads `descriptor.key` / `descriptor.defaultValue`
 * from the declaration directly above it, so the pair cannot drift, and
 * `flags.test.ts` asserts each key appears exactly once in this file and that no
 * read takes a string literal — the only assertion that can tell a descriptor
 * read from a hardcoded string that happens to be equal today. Flag keys have no
 * cross-site-identity guard (cache keys do), so a second spelling would be
 * invisible to every other gate in the repo.
 *
 * THE DOOR IS HEADLESS, guarded transitively rather than by inspection.
 * `scripts/monorepo-migration/lib/package-ui-zone.ts` admits
 * `@heidi/runtime/feature-flags` ONLY from this file out of everything under
 * `packages/tasks/src`; `manifest-boundary.test.ts` walks this module's graph
 * ACROSS package boundaries and refuses `@heidi/design-system` and this
 * package's own `/ui` zone anywhere in it, and separately proves the door
 * imports in a bare Node process with no DOM and no side effects.
 *
 * PACKAGE UI IMPORTS THESE RELATIVELY (`../flags`), never through the door — a
 * package that imports itself by name resolves through node_modules and reads as
 * an undeclared external dependency. The app barrel `@/features/flags`
 * re-exports the getters from `@heidi/tasks/flags`, so its importers — 30
 * production files across the five names below — and their `jest.mock` literals
 * are untouched. Some of those consumers are under
 * `src/features/integrations-letters/letter-sender-v2/**` rather than a tasks
 * root; those are cross-root READS of a tasks-owned flag and stay exactly where
 * they are, resolving through the barrel re-export. The clinical-tasks
 * consumers that used to be counted beside them are INSIDE this package now
 * now and read the getters relatively, as `../flags`.
 *
 * WHAT DOES NOT BELONG HERE — one of this domain's six app-barrel getters is
 * NOT below, and it is named rather than silently skipped.
 * `useIsInboxClinicalTasksEnabled` composes `useIsDocumentInboxEnabled()`, a
 * FOREIGN domain's getter (integrations / document-inbox). Ruling 36 (b) is
 * explicit: never import another domain's flag module — you would bind their
 * whole door for one boolean and invert the dependency direction for it, and the
 * resolved boolean crosses as a prop at the composition point that already owns
 * it. So that getter stays app-side, where the composition it performs is legal,
 * and its own forceAll branch stays with it.
 */
import { defineFeatureFlag, useBooleanFlag } from '@heidi/runtime/feature-flags';

// ---------------------------------------------------------------------------
// The declarations. `key` is a FROZEN EXTERNAL IDENTITY — Flagsmith targets the
// flag by it, so renaming one is switching flags, never a refactor.
//
// `builds: 'all'` on all five, for one measured reason: clinical tasks are a
// clinical face of the product on every build profile — the task list, the task
// panel, the assignee selector and the prescription surfaces all render on the
// on-prem appliance today — and nothing these gate reads an Apple or on-prem
// gate. The D6 default (`'cloud-only'`) would withhold from the appliance five
// surfaces it already ships.
// ---------------------------------------------------------------------------

/** Task assignment: assigning clinical tasks to team members. */
export const taskEnableAssignmentFlag = defineFeatureFlag({
  key: 'release-task-enable-assignment',
  defaultValue: false,
  description: 'Clinical task assignment to team members',
  builds: 'all',
});

/**
 * MUL-3055: team list visibility in the clinical task assignee selector. When
 * enabled, org teams appear as selectable assignees alongside individual
 * members.
 */
export const taskEnableTeamAssignmentFlag = defineFeatureFlag({
  key: 'release-task-enable-team-assignment',
  defaultValue: false,
  description: 'Org teams as selectable clinical task assignees',
  builds: 'all',
});

/**
 * MUL-2413: team assignment in the clinical task assignee selector, behind the
 * FGA generic-grant path.
 *
 * ZERO PRODUCTION CONSUMERS at conversion, and that is recorded rather than
 * acted on. A getter nobody reads is a candidate for retirement, but retiring a
 * flag is a `docs/feature-flag-cleanups.md` change with a PostHog/Flagsmith step
 * behind it — a different decision from where the declaration lives. So it moves
 * with its siblings and is not deleted here.
 */
export const tasksEnableAssignmentFgaGenericGrantFlag = defineFeatureFlag({
  key: 'release-tasks-enable-assignment-fga-generic-grant',
  defaultValue: false,
  description: 'Clinical task team assignment via the FGA generic grant',
  builds: 'all',
});

/**
 * Rich-text clinical task titles and descriptions: when ON, the fields use a
 * TipTap-based editor with markdown support instead of plain textareas.
 * Backward compatible — plain text is valid markdown, so existing tasks render
 * in the new editor without migration.
 */
export const clinicalTaskRichTextFlag = defineFeatureFlag({
  key: 'release-clinical-tasks-rich-text-editor',
  defaultValue: false,
  description: 'Rich-text editor for clinical task title and description',
  builds: 'all',
});

/**
 * Apollo prescription-category slice. Gates the `prescription` option in the
 * clinical-task category dropdown, the inline prescription form rendered on
 * tasks with `category === "prescription"`, and calls to
 * `/api/v2/prescriptions/*`. Mirrors the backend
 * `IS_APOLLO_PRESCRIPTION_CATEGORY_ENABLED` helper (ml-scribe
 * `features/release/release_apollo_prescription_category.py`). Default OFF —
 * every consumer must call the getter before rendering prescription-aware UI.
 *
 * RECLASSIFIED TO TASKS in the fan-out, and the reclassification is the point. The
 * key reads coding-ish by prefix (`release-apollo-…` alongside the Apollo
 * coding work), but ownership follows the SURFACE, not the key's spelling: all
 * five production consumers — the create sheet, the edit sheet, the panel's
 * create item, the prescription section and the category selector — are
 * clinical-task surfaces, and every one of them is now inside this package
 * itself, which is the strongest form the claim can take.
 */
export const apolloPrescriptionCategoryFlag = defineFeatureFlag({
  key: 'release-apollo-prescription-category',
  defaultValue: false,
  description: 'Apollo prescription category on clinical tasks',
  builds: 'all',
});

// ---------------------------------------------------------------------------
// The getters. Each reads the pair off the descriptor above it.
//
// ONE DISCLOSED BEHAVIOURAL DELTA, common to all five and a gain rather than a
// risk: `useBooleanFlag` honours the injected `forceEnableAll` override, which
// the `useBooleanFlagValue` calls these replace did not. That is the choke point
// the door exists for (design D8) and the knob is dev-only.
// ---------------------------------------------------------------------------

/**
 * Task assignment. The widest-consumed name in this batch — 13 production
 * files, every one of which keeps importing it from `@/features/flags`.
 */
export const useIsTaskEnableAssignmentEnabled = (): boolean =>
  useBooleanFlag(taskEnableAssignmentFlag.key, taskEnableAssignmentFlag.defaultValue);

/** Team list visibility in the clinical task assignee selector (MUL-3055). */
export const useIsTaskEnableTeamAssignmentEnabled = (): boolean =>
  useBooleanFlag(
    taskEnableTeamAssignmentFlag.key,
    taskEnableTeamAssignmentFlag.defaultValue
  );

/** Team assignment behind the FGA generic-grant path (MUL-2413). */
export const useIsTasksEnableAssignmentFgaGenericGrantEnabled = (): boolean =>
  useBooleanFlag(
    tasksEnableAssignmentFgaGenericGrantFlag.key,
    tasksEnableAssignmentFgaGenericGrantFlag.defaultValue
  );

/** Rich-text clinical task title and description fields. */
export const useIsClinicalTaskRichTextEnabled = (): boolean =>
  useBooleanFlag(clinicalTaskRichTextFlag.key, clinicalTaskRichTextFlag.defaultValue);

/**
 * The Apollo prescription-category slice.
 *
 * THE forceAll BRANCH COLLAPSED ON THE MOVE, and that is behaviour-preserving
 * rather than a dropped feature. The app-side body was a `useBooleanFlagValue`
 * read wrapped in `if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS
 * === 'true') return true;` — a hand-rolled dev escape hatch whose own comment
 * asked for it to be removed "once an OpenFeature-wide force-all provider
 * exists". It does: `flags.ts` may not read `process.env`, and it does not need
 * to, because `useBooleanFlag` returns
 * `getFeatureFlagsConfig().forceEnableAll || value` and the app injects that
 * boolean from the SAME env var at `src/composition/wiring/runtime-wiring.ts`. The manual
 * read disappears; the behaviour does not.
 */
export const useIsApolloPrescriptionCategoryEnabled = (): boolean =>
  useBooleanFlag(
    apolloPrescriptionCategoryFlag.key,
    apolloPrescriptionCategoryFlag.defaultValue
  );
